<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "@/lib/axios";
import Swal from "sweetalert2";

const isLoading = ref(true);
const isRefreshing = ref(false);
const therapists = ref([]);
const locations = ref([]);
const activeTab = ref("therapists");
const search = ref("");
const retrying = ref({});
const error = ref(null);
const validTimezones = ref(undefined);
const moduleNameToInternalName = {
    Therapists: "CustomModule2",
    therapist: "CustomModule2",
    Staff_Members: "CustomModule2",
    Locations: "CustomModule17",
    location: "CustomModule17"
};

const CRM_BASE_URL = "https://crm.zoho.com/crm/org805341821/tab/";

const syncRecords = computed(() => {
    return activeTab.value === "therapists" ?
        therapists.value :
        locations.value;
});

const filteredRecords = computed(() => {
    const term = search.value.trim().toLowerCase();
    if (!term) return syncRecords.value;

    return syncRecords.value.filter((record) => {
        return [
            record.first_name,
            record.last_name,
            record.name,
            record.Catalyst_Sync_Fail_Reason,
            record.id
        ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term));
    });
});

function getRecordType() {
    return activeTab.value === "therapists" ?
        "therapist" :
        "location";
}

function getReason(record) {
    return String(record?.Catalyst_Sync_Fail_Reason || "").trim();
}

function getReasonInfo(reason) {
    const value = reason.toLowerCase();

    if (value === "zoho_crm_id is required")
        return {
            title: "Internal Error",
            hint: "Upsert workflow failed to execute correctly (Zoho CRM ID was never provided). Please, contact development team.",
            action: null
        };

    if (value.includes("invalid therapist crm id=") || value.includes("invalid location crm id="))
        return {
            title: "Internal Error",
            hint: "Upsert workflow failed to execute correctly (Zoho CRM ID or record is invalid). Please, contact development team.",
            action: null
        };

    if (value.includes("invalid therapist timezone '") || value.includes("invalid location timezone '"))
        return {
            title: "Required Field Missing",
            hint: "Therapist timezone is required but not provided.",
            action: "Set Timezone"
        };

    if (value === "invalid location therapist.")
        return {
            title: "Required Field Missing",
            hint: "Location therapist is required but not provided.",
            action: "Open CRM"
        };

    return {
        title: "Unexpected Error",
        hint: "Failure reason is either unique or unexpected. Please, contact development team.",
        action: null
    };
}

async function retryRecord(record, type) {
    if (!record?.id || retrying.value[record.id]) throw new Error("Invalid record or retry in progress");

    retrying.value = { ...retrying.value, [record.id]: true };

    try {
        const response = await axios.post("/server/api/admin/sync_health/retry", {
            module: type === "therapist" ? "Staff_Members" : "Location",
            zoho_crm_id: record.id
        });

        console.log("Retry response:", response.data);

        if (response.data?.status && response.data?.status !== "success")
            throw new Error(response.data?.message || "Failed to retry synchronization.");

        // Retry succeeded — remove the record locally
        if (type === "therapist") therapists.value = therapists.value.filter((item) => item.id !== record.id);
        else locations.value = locations.value.filter((item) => item.id !== record.id);

        return true;
    } catch (err) {
        console.error("Failed to retry sync:", err);

        await Swal.fire({
            icon: "error",
            title: "Retry failed",
            text: err?.response?.data?.message ??
                err?.response?.data?.message?.message ??
                err?.message ??
                "Failed to retry synchronization."
        });

        return false;
    } finally {
        const updated = { ...retrying.value };
        delete updated[record.id];
        retrying.value = updated;
    }
}

async function quickAction(record, type, action) {
    if (action === "Set Timezone") {
        Swal.fire({
            title: "Specify timezone",
            icon: "question",
            input: "select",
            inputOptions: validTimezones.value,
            inputPlaceholder: "Select a timezone",
            showCancelButton: true,
            confirmButtonText: "Set Timezone"
        }).then(async (result) => {
            if (result.isConfirmed && result.value) {
                Swal.fire({
                    title: "Updating Record...",
                    html: "Please wait...",
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading(); // Activates the loading spinner
                    }
                });
                // Update the record with the selected timezone
                const r = await axios.post("/server/api/admin/sync_health/update_crm_record", {
                    module: type === "therapist" ? "Staff_Members" : "Location",
                    record_id: record.id,
                    payload: { [`Timezone${type === "therapist" ? "s" : ""}`]: validTimezones.value[result.value] }
                });
                console.log("update_crm_record", r);
                const retryResponse = await retryRecord(record, type);
                console.log({ retryResponse: retryResponse });
                Swal.close();
            }
        });
    }

    // if (action === "Set Therapist") {
    //     Swal.fire({
    //         title: "Specify therapist",
    //         icon: "question",
    //         input: "select",
    //         inputOptions: therapists.value,
    //         inputPlaceholder: "Select a therapist",
    //         showCancelButton: true,
    //         confirmButtonText: "Set Therapist"
    //     }).then(async (result) => {
    //         if (result.isConfirmed && result.value) {
    //             Swal.fire({
    //                 title: "Updating Record...",
    //                 html: "Please wait...",
    //                 allowOutsideClick: false,
    //                 didOpen: () => {
    //                     Swal.showLoading(); // Activates the loading spinner
    //                 }
    //             });
    //             // Update the record with the selected therapist
    //             const r = await axios.post("/server/api/admin/sync_health/update_crm_record", {
    //                 module: type === "therapist" ? "Staff_Members" : "Location",
    //                 record_id: record.id,
    //                 payload: { [`Therapist${type === "therapist" ? "s" : ""}`]: therapists.value[result.value] }
    //             });
    //             console.log("update_crm_record", r);
    //             const retryResponse = await retryRecord(record, type);
    //             console.log({ retryResponse: retryResponse });
    //             Swal.close();
    //         }
    //     });
    // }

    if (action === "Open CRM") {
        window.open(`${CRM_BASE_URL}${moduleNameToInternalName[getRecordType()]}/${record.id}`, "_blank");
    }

    if (action === "Retry") {
        await retryRecord(record, type);
        return;
    }
}

async function fetchSyncHealth(showLoader = true) {
    if (showLoader) isLoading.value = true;
    else isRefreshing.value = true;

    error.value = null;

    try {
        const response = await axios.get("/server/api/admin/sync_health/index");
        console.log({ syncHealthData: response.data });

        if (response.data?.status !== "success")
            throw new Error(response.data?.message || "Failed to load sync health");

        const data = response.data?.data || {};

        validTimezones.value = data.valid_timezones;
        therapists.value = data.therapists;
        locations.value = data.locations;
        console.log({ therapists: therapists.value, locations: locations.value });
    } catch (err) {
        console.error("Failed to load sync health:", err);

        error.value =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to load synchronization health.";

        if (showLoader) {
            await Swal.fire({
                icon: "error",
                title: "Failed to load Sync Health",
                text: error.value
            });
        }
    } finally {
        isLoading.value = false;
        isRefreshing.value = false;
    }
}

async function refresh() {
    await fetchSyncHealth(false);
}

onMounted(async () => {
    await fetchSyncHealth();
});
</script>

<template>
    <div class="bg-white rounded-lg shadow p-6">
        <!-- Header -->
        <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <div>
                <h2 class="text-xl font-semibold">
                    Sync Health
                </h2>
                <h6 class="text-sm text-gray-600">
                    CRM synchronization status for therapists and locations
                </h6>
            </div>

            <div class="flex items-center space-x-2">
                <button
                    @click="refresh"
                    class="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    :disabled="isRefreshing"
                >
                    {{ isRefreshing ? "Refreshing..." : "Refresh" }}
                </button>
            </div>
        </div>

        <!-- Summary -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <button
                @click="activeTab = 'therapists'"
                class="border rounded-lg p-4 text-left hover:bg-gray-50 transition"
                :class="{
                    'ring-2 ring-blue-500': activeTab === 'therapists'
                }"
            >
                <div class="flex justify-between items-center">
                    <div>
                        <div class="text-sm text-gray-500">
                            Therapists
                        </div>
                        <div class="text-2xl font-semibold">
                            {{ therapists.length }}
                        </div>
                    </div>

                    <span
                        class="px-3 py-1 rounded-full text-sm font-medium"
                        :class="
                            therapists.length > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        "
                    >
                        {{ therapists.length }}
                        {{ therapists.length === 1 ? "failure" : "failures" }}
                    </span>
                </div>
            </button>

            <button
                @click="activeTab = 'locations'"
                class="border rounded-lg p-4 text-left hover:bg-gray-50 transition"
                :class="{
                    'ring-2 ring-blue-500': activeTab === 'locations'
                }"
            >
                <div class="flex justify-between items-center">
                    <div>
                        <div class="text-sm text-gray-500">
                            Locations
                        </div>
                        <div class="text-2xl font-semibold">
                            {{ locations.length }}
                        </div>
                    </div>

                    <span
                        class="px-3 py-1 rounded-full text-sm font-medium"
                        :class="
                            locations.length > 0
                                ? 'bg-red-100 text-red-700'
                                : 'bg-green-100 text-green-700'
                        "
                    >
                        {{ locations.length }}
                        {{ locations.length === 1 ? "failure" : "failures" }}
                    </span>
                </div>
            </button>
        </div>

        <!-- Toolbar -->
        <input
            v-model="search"
            type="search"
            placeholder="Search..."
            class="border rounded-lg px-3 py-2 w-full"
        >

        <!-- Error -->
        <div
            v-if="error"
            class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
        >
            {{ error }}
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
            <table
                v-if="filteredRecords.length"
                class="w-full"
            >
                <thead>
                    <tr class="border-b">
                        <th class="px-4 py-2 text-left">
                            {{ getRecordType() === "therapist" ? "Therapist" : "Location" }}
                        </th>

                        <th class="px-4 py-2 text-left">
                            Failure Reason
                        </th>

                        <th class="px-4 py-2 text-left">
                            Hint / Quick Action
                        </th>

                        <th class="px-4 py-2 text-right">
                            Actions
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="record in filteredRecords"
                        :key="record.id"
                        class="border-b"
                    >
                        <!-- Name -->
                        <td class="px-4 py-3">
                            <a
                                class="font-medium hover:text-blue-600 text-blue-500 hover:underline"
                                :href="`${CRM_BASE_URL}${moduleNameToInternalName[getRecordType()]}/${record.id}`"
                                target="_blank"
                            >
                                {{ record.Name }}
                            </a>
                            <br />
                            <a
                                v-if="getRecordType() === 'location' && record.Resource"
                                class="font-medium text-xs hover:text-blue-600 text-blue-500 hover:underline"
                                :href="`${CRM_BASE_URL}${moduleNameToInternalName.therapist}/${record.Resource.id}`"
                                target="_blank"
                            >
                                Therapist: {{ record.Resource.name }}
                            </a>
                        </td>

                        <!-- Reason -->
                        <td class="px-4 py-3 max-w-sm">
                            <span class="text-sm">
                                {{ getReason(record) }}
                            </span>
                        </td>

                        <!-- Hint -->
                        <td class="px-4 py-3 min-w-[300px]">
                            <div class="text-sm font-bold mb-1">
                                {{ getReasonInfo(getReason(record)).title }}
                            </div>

                            <div class="text-sm text-gray-600 mb-2">
                                {{ getReasonInfo(getReason(record)).hint }}
                            </div>

                            <button
                                v-if="getReasonInfo(getReason(record)).action"
                                @click="
                                    quickAction(
                                        record,
                                        getRecordType(),
                                        getReasonInfo(getReason(record)).action
                                    )
                                "
                                class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                                {{ getReasonInfo(getReason(record)).action }} →
                            </button>
                        </td>

                        <!-- Actions -->
                        <td class="px-4 py-3 text-right whitespace-nowrap">
                            <div class="flex justify-end items-center gap-2">
                                <button
                                    @click="retryRecord(record, getRecordType())"
                                    class="px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
                                    :disabled="retrying[record.id]"
                                >
                                    {{
                                        retrying[record.id] ? "Retrying..." : "Retry"
                                    }}
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Empty -->
            <div
                v-else
                class="text-center py-12 text-gray-500"
            >
                <template v-if="search">
                    No {{ activeTab }} match "{{ search }}".
                </template>

                <template v-else-if="syncRecords.length">
                    No synchronization failures found.
                </template>

                <template v-else-if="isRefreshing">
                    Refreshing...
                </template>

                <template v-else>
                    No {{ activeTab }} found.
                </template>
            </div>
        </div>
    </div>

    <!-- Loader -->
    <dialog
        v-show="isLoading"
        class="fixed inset-0 z-50 flex flex-col items-center justify-center w-full h-full bg-gray-900 bg-opacity-75"
    >
        <svg
            aria-hidden="true"
            class="w-24 h-24 animate-spin"
            viewBox="0 0 100 101"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4011 91.5095 50 91.5095C72.5989 91.5095 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5989 9.67226 50 9.67226C27.4011 9.67226 9.08144 27.9921 9.67226 50.5908Z"
                fill="slategray"
            />
            <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.293 28.8227 92.871 24.3692 89.816 20.348C85.845 15.1192 80.882 10.7238 75.212 7.41289C69.542 4.10194 63.275 1.94025 56.7698 1.05124C51.766 0.367541 46.697 0.446843 41.734 1.27873C39.261 1.69328 37.813 4.19778 38.450 6.62326C39.087 9.04874 41.569 10.4717 44.050 10.1071C47.851 9.54855 51.719 9.52689 55.540 10.0491C60.864 10.7766 65.992 12.5457 70.633 15.2552C75.273 17.9648 79.334 21.5619 82.584 25.841C84.917 28.9121 86.799 32.2913 88.181 35.8758C89.083 38.2158 91.542 39.6783 93.9676 39.0409Z"
                fill="blue"
            />
        </svg>
    </dialog>
</template>

<style scoped>
select,
input[type="radio"],
input[type="checkbox"] {
    cursor: pointer;
}

button:disabled {
    cursor: not-allowed;
}

table {
    min-width: 1000px;
}
</style>
